import { useSelector } from '@xstate/react';
import { type ReactNode } from 'react';
import { FormProvider } from 'react-hook-form';
import type { SnapshotFrom } from 'xstate';
import { Button, DatePicker } from '~/components';
import {
  earliestDateOptions,
  formatDate,
  isStartDateOnly,
} from '~/features/employer/provider-search/availabilityOptionUtils';
import {
  availabilityWindowError,
  localDate,
  ProviderAvailabilityOptions,
  selectedAvailabilityEntryIds,
  selectionOptionKey,
  StartOptionChoice,
} from '~/features/employer/provider-search/ProviderAvailabilityOptions';
import { ModalDialog } from '~/features/vault/documents/ModalDialog';
import { useCustomForm } from '~/hooks/useCustomForm';
import type { ServiceOpportunity } from './schemas';
import { serviceBeginMachine } from './serviceBegin.machine';
import { useServiceResponseActors } from './ServiceResponseBoundary';
import type { ServiceResponseActors } from './serviceResponseSession.machine';

type Snapshot = SnapshotFrom<typeof serviceBeginMachine>;
const selectOpen = (s: Snapshot) => s.context.open;
const selectSource = (s: Snapshot) => s.context.source;
const selectListing = (s: Snapshot) => s.context.listing;
const selectKeys = (s: Snapshot) => s.context.selectedKeys;
const selectError = (s: Snapshot) => s.context.error;
const selectRevision = (s: Snapshot) => s.context.revision;
const selectBusy = (s: Snapshot) => s.matches('sending') || s.matches('recovering');
const selectLoading = (s: Snapshot) => s.matches('loading');
const selectReady = (s: Snapshot) => s.matches('ready');
const selectUncertain = (s: Snapshot) => s.matches('uncertain');
type Window = { start: string; end: string };
const SERVICE_BEGIN_PREVIEW_LIMIT = 10;

export const ServiceBeginFlow = ({
  responseId,
  children,
}: {
  responseId: number;
  children: (controls: {
    open: (opportunity: ServiceOpportunity) => void;
    revision: number;
    busy: boolean;
  }) => ReactNode;
}) => {
  const actors = useServiceResponseActors(responseId);
  return actors ? <OwnedBeginFlow actor={actors.begin}>{children}</OwnedBeginFlow> : null;
};

const OwnedBeginFlow = ({
  actor,
  children,
}: {
  actor: ServiceResponseActors['begin'];
  children: (controls: {
    open: (opportunity: ServiceOpportunity) => void;
    revision: number;
    busy: boolean;
  }) => ReactNode;
}) => {
  const open = useSelector(actor, selectOpen);
  const source = useSelector(actor, selectSource);
  const listing = useSelector(actor, selectListing);
  const keys = useSelector(actor, selectKeys);
  const error = useSelector(actor, selectError);
  const revision = useSelector(actor, selectRevision);
  const busy = useSelector(actor, selectBusy);
  const loading = useSelector(actor, selectLoading);
  const ready = useSelector(actor, selectReady);
  const uncertain = useSelector(actor, selectUncertain);
  const snapshot = actor.getSnapshot();
  const methods = useCustomForm<Window>({
    defaultValues: {
      start: snapshot.context.start,
      end: snapshot.context.end,
    },
  });
  const today = localDate(new Date());
  // [MCV-1837] Part-time and full-time listings are searched by start date alone: no End date.
  const startDateOnly = isStartDateOnly(source?.listing.listingKind ?? '');
  const startDate = listing
    ? earliestDateOptions(listing.availability?.selectionOptions ?? [])[0]?.availabilityDate
    : undefined;
  return (
    <>
      {children({
        open: (opportunity) => actor.send({ type: 'OPEN', opportunity }),
        revision,
        busy: busy || uncertain,
      })}
      {uncertain && !open && source && (
        <div className="m-4 space-y-2">
          <p role="status">The negotiation outcome for {source.listing.title} is unconfirmed.</p>
          <Button
            type="button"
            label="Review original selection"
            onClick={() => actor.send({ type: 'OPEN', opportunity: source })}
          />
        </div>
      )}
      <ModalDialog
        open={open}
        title="Select service availability"
        xButton
        closeDisabled={busy}
        onClose={() => actor.send({ type: 'CLOSE' })}
      >
        <p className="body-text-sb">{source?.listing.title}</p>
        <p className="my-3 text-sm">
          Selecting dates does not reserve them. Begin negotiation creates an offer on this response
          and holds the selected availability. It does not share identities or accept an offer.
        </p>
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit((window) => {
              if (!ready) return;
              // Same rule as Search Providers: the search API rejects a past start.
              if (window.start && window.start < today) {
                methods.setError('start', {
                  type: 'validate',
                  message: "Start date can't be in the past.",
                });
                return;
              }
              if (!window.start) {
                methods.setError('start', { type: 'validate', message: 'Choose a start date.' });
                return;
              }
              const windowError = startDateOnly
                ? null
                : !window.end
                  ? 'Choose an end date on or after the start date.'
                  : availabilityWindowError(window.start, window.end);
              if (windowError) {
                methods.setError('end', { type: 'validate', message: windowError });
                return;
              }
              actor.send({ type: 'SEARCH', window });
            })}
          >
            <fieldset disabled={!ready} className="space-y-3">
              <DatePicker name="start" label="Start date" required minDate={today} />
              {!startDateOnly && <DatePicker name="end" label="End date" required />}
              <Button type="submit" label="Search availability" disabled={!ready} />
            </fieldset>
          </form>
        </FormProvider>
        <p role="status" aria-live="polite" className="my-3 min-h-5 text-sm">
          {error ??
            (loading
              ? 'Loading availability…'
              : ready && !listing
                ? 'No current availability for this listing in the selected window.'
                : '')}
        </p>
        {listing && isStartDateOnly(listing.listingKind) && (
          <p className="my-3 text-sm">
            Start date: {startDate ? formatDate(startDate) : 'Not specified'}
          </p>
        )}
        {listing && isStartDateOnly(listing.listingKind) && (
          <StartOptionChoice
            listing={listing}
            selectedKeys={new Set(keys)}
            disabled={!ready}
            onSelect={(option) => actor.send({ type: 'CHOOSE', key: selectionOptionKey(option) })}
          />
        )}
        {listing && !isStartDateOnly(listing.listingKind) && (
          <ProviderAvailabilityOptions
            listing={listing}
            previewLimit={SERVICE_BEGIN_PREVIEW_LIMIT}
            viewAllMode="inline"
            selectedKeys={new Set(keys)}
            disabled={!ready}
            onToggle={(option) => actor.send({ type: 'TOGGLE', key: selectionOptionKey(option) })}
          />
        )}
        {uncertain ? (
          <div className="flex flex-wrap gap-2">
            <p className="w-full text-sm">
              Discarding clears the locally saved selection and recovery. It does not cancel or undo
              server work, including any availability hold.
            </p>
            <Button
              type="button"
              label="Refresh negotiation outcome"
              onClick={() => actor.send({ type: 'REFRESH' })}
            />
            <Button
              type="button"
              label="Retry original selection"
              onClick={() => actor.send({ type: 'RETRY' })}
            />
            <Button
              type="button"
              colorVariant="outline"
              label="Discard local selection"
              onClick={() => actor.send({ type: 'DISCARD' })}
            />
          </div>
        ) : (
          <Button
            type="button"
            label={busy ? 'Checking negotiation…' : 'Begin negotiation'}
            disabled={
              !ready ||
              !listing ||
              selectedAvailabilityEntryIds(listing, new Set(keys)).length === 0
            }
            onClick={() => actor.send({ type: 'BEGIN' })}
          />
        )}
      </ModalDialog>
    </>
  );
};
